import React, { memo, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Card, Button, Input, Table, Avatar, Spin, Select, Tag } from 'antd'
import { DownloadOutlined, ClockCircleOutlined } from '@ant-design/icons'
import './TopCreditUsers.scss'

const { Search } = Input
const { Option } = Select

const formatModelName = (modelString) => {
  if (!modelString) return ''
  return modelString
    .replace(/-/g, ' ')
    .replace(/\bpreview\b/gi, '')
    .replace(/\blatest\b/gi, '')
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const TopCreditUsers = memo(({
  title = 'Top Credit Users',
  valueTitle = 'Average Cost',
  isCreditsTable = false,
  hideRank = false,
  data,
  loading,
  onExportClick,
  onHistoryClick,
  onSearch,
  searchTerm,
  pagination,
  onPageChange,
  tableFilter,
  onTableFilterChange,
  activeTypes = [],
  selectedType,
  onTypeChange
}) => {
  const timeFilters = ['All Time', 'Today', 'This Week', 'This Month', 'This Year']

  const tableData = useMemo(() => {
    if (!data?.topUsers) return []

    return data.topUsers.map((user, index) => ({
      key: user.id || index,
      rank: user.rank || (pagination?.current - 1) * pagination?.pageSize + index + 1,
      user: user.name || 'Unknown User',
      email: user.email || '',
      avatar: user.avatar || '',
      credits: user.totalCreditsUsed || 0,
      level: user.level || 'N/A',
      tokens: user.tokens || 0,
      model: user.model || '',
      prompt: user.prompt || ''
    }))
  }, [data, pagination])

  const columns = useMemo(() => {
    const cols = []
    
    if (!hideRank) {
      cols.push({
        title: 'Rank #',
        dataIndex: 'rank',
        key: 'rank',
        width: 100,
        render: rank => {
          const suffix = rank === 1 ? 'st' : rank === 2 ? 'nd' : rank === 3 ? 'rd' : 'th'
          return (
            <span className="rank-cell">
              {rank}
              <span className="rank-suffix">{suffix}</span>
            </span>
          )
        }
      })
    }

    cols.push({
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (user, record) => (
        <div className="user-info" style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar src={record.avatar} className="user-avatar" style={{ marginRight: '8px' }}>
            {user?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div className="user-details" style={{ display: 'flex', flexDirection: 'column' }}>
            <span className="user-name" style={{ fontWeight: 500 }}>{user}</span>
          </div>
        </div>
      )
    })

    if (!isCreditsTable) {
      cols.push({
        title: 'Model Used',
        key: 'model',
        render: (_, record) => {
          return record.model ? (
            <Tag color="geekblue" style={{ borderRadius: '4px', border: 'none', background: '#f0f5ff', padding: '2px 8px', fontSize: '13px', margin: 0 }} title={record.model}>
              {formatModelName(record.model)}
            </Tag>
          ) : <span style={{ color: '#aaa' }}>-</span>
        }
      })
    }

    cols.push({
      title: valueTitle,
      dataIndex: 'credits',
      key: 'credits',
      render: credits => {
        if (typeof credits === 'number') {
          return credits > 0 && credits < 1
            ? Number(credits).toLocaleString(undefined, { maximumFractionDigits: 6 })
            : Number(credits).toLocaleString()
        }
        return credits
      }
    })

    return cols
  }, [valueTitle, isCreditsTable])

  return (
    <Card className="top-credit-users-card">
      <div className="top-credit-users-content">
        <div className="section-header">
          <h3 className="section-title">{title}</h3>
          {activeTypes.length > 0 && !isCreditsTable && (
            <div className="type-filter-container">
              <span className="type-label">Type:</span>
              <Select
                value={selectedType}
                onChange={onTypeChange}
                className="type-select"
                placeholder="All Services"
              >
                {activeTypes.map(type => {
                  const colorMap = {
                    'ai-image': '#5B6EE2',
                    'ai_video': '#C9D3FA',
                    audio: '#3875F6',
                    course: '#44959A',
                    podcast: '#EB3B88',
                    maze: '#9FD4FB',
                    sharebot: '#2DCCB9'
                  }

                  let displayName = type.charAt(0).toUpperCase() + type.slice(1)
                  if (type === 'ai-image') displayName = 'AI-Image'
                  else if (type === 'ai_video') displayName = 'Ai-Video'

                  return (
                    <Option key={type} value={type}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colorMap[type] || '#888', display: 'inline-block' }} />
                        {displayName}
                      </div>
                    </Option>
                  )
                })}
              </Select>
            </div>
          )}
        </div>

        <div className="filters-section">
          <div className="time-filters">
            {/* {timeFilters.map(filter => (
              <Button
                key={filter}
                type={tableFilter === filter ? 'primary' : 'default'}
                onClick={() => onTableFilterChange(filter)}
                className="time-filter-button"
              >
                {filter}
              </Button>
            ))} */}
          </div>
          <div className="header-actions">
            <Button
              onClick={onExportClick}
              disabled={tableData.length === 0}
            >
              <DownloadOutlined /> Export Report
            </Button>
            {/* <Button
              onClick={onHistoryClick}
            >
              <ClockCircleOutlined /> History
            </Button> */}
            <Search
              placeholder="Search by name..."
              allowClear
              value={searchTerm}
              onChange={e => onSearch(e.target.value)}
              onSearch={value => onSearch(value)}
            />
          </div>
        </div>

        <div className="table-section">
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={{
                ...pagination,
                onChange: onPageChange,
                onShowSizeChange: onPageChange,
                showSizeChanger: false,
                pageSizeOptions: [10, 20, 50, 100]
              }}
              className="users-table"
            />
          </Spin>
        </div>
      </div>
    </Card>
  )
})

export default TopCreditUsers
